import React from "react";
import { Tile } from "./Tile";
import { EmptyDomino } from "../engine/types";
import { useElementSize } from "./responsive";

export interface HandProps {
  hand: number[];
  selectedCard: number | null;
  playableCards: Set<number>;
  onPickCard: (card: number) => void;
  isYourTurn: boolean;
  hint?: string;
}

export const Hand: React.FC<HandProps> = ({
  hand,
  selectedCard,
  playableCards,
  onPickCard,
  isYourTurn,
  hint,
}) => {
  const [ref, size] = useElementSize<HTMLDivElement>();
  const visible = hand.filter((c) => c !== EmptyDomino && c < 28);
  const padding = 8;
  const gap = 6;
  const widthBudget = Math.max(0, size.w - padding * 2 - gap * Math.max(0, visible.length - 1));
  const perTileWidth = visible.length > 0 ? widthBudget / visible.length : 40;
  // Vertical tiles: short (tileSize/2) lies along the row, long (tileSize) is the height.
  const tileSize = Math.max(36, Math.min(perTileWidth * 2, size.h - padding * 2 - 24, 140));

  return (
    <div
      ref={ref}
      style={{
        position: "relative",
        width: "100%",
        height: "100%",
        background: "rgba(0,0,0,0.35)",
        borderRadius: 12,
        padding,
        boxSizing: "border-box",
        display: "flex",
        flexDirection: "column",
        gap: 4,
        justifyContent: "center",
      }}
    >
      <div
        style={{
          color: isYourTurn ? "#FED100" : "#cfead4",
          fontSize: 12,
          fontWeight: 600,
          opacity: 0.9,
          minHeight: 16,
        }}
      >
        {hint ?? (isYourTurn ? "Your turn" : "Your hand")}
      </div>
      <div
        style={{
          display: "flex",
          gap,
          alignItems: "center",
          justifyContent: "center",
          flexWrap: "nowrap",
          overflowX: "auto",
          overflowY: "hidden",
        }}
      >
        {visible.map((c, i) => {
          const playable = playableCards.has(c);
          return (
            <Tile
              key={`${i}-${c}`}
              card={c}
              size={tileSize}
              orientation="vertical"
              highlighted={selectedCard === c}
              dimmed={isYourTurn && !playable}
              onClick={() => onPickCard(c)}
            />
          );
        })}
        {visible.length === 0 && (
          <div style={{ color: "#cfead4", opacity: 0.7 }}>No tiles.</div>
        )}
      </div>
    </div>
  );
};
